// S15 — Folders (Media Center) BE client.
//
// BE endpoints (under `/api/v1/admin/folders`):
//   GET    /tree         — full hierarchy (sidebar)
//   GET    /{id}         — detail
//   POST   /             — create  body: { name, parentFolderId }
//   PUT    /{id}/rename  — body: { newName, rowVersion }
//   POST   /{id}/move    — body: { newParentFolderId }
//   DELETE /{id}         — soft delete

import { http, unwrap, unwrapList, type ActionResponse } from './http';
import type { components } from '@/types/api';

export type FolderBeDto = components['schemas']['FolderDto'];
export type FolderTreeNodeBeDto = components['schemas']['FolderTreeNodeDto'];

export interface FolderItem {
  id: string;
  name: string;
  parentFolderId: string | null;
  path: string;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  rowVersion: string | null;
}

export interface FolderTreeNode {
  id: string;
  name: string;
  path: string;
  parentFolderId: string | null;
  /**
   * ISSUE-62 — BE now embeds the folder's `rowVersion` directly in tree
   * nodes, so the FE rename flow no longer needs a follow-up `GET /folders/{id}`
   * just to grab the concurrency token.
   */
  rowVersion: string | null;
  children: FolderTreeNode[];
}

function mapFolder(d: FolderBeDto): FolderItem {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    parentFolderId: d.parentFolderId ?? null,
    path: d.path ?? '',
    createdByUserId: d.createdByUserId ?? '',
    createdAt: d.createdAt ?? '',
    updatedAt: d.updatedAt ?? '',
    rowVersion: d.rowVersion ?? null,
  };
}

function mapTreeNode(d: FolderTreeNodeBeDto): FolderTreeNode {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    path: d.path ?? '',
    parentFolderId: d.parentFolderId ?? null,
    // ISSUE-62 — propagated from BE tree response.
    rowVersion: d.rowVersion ?? null,
    children: (d.children ?? []).map(mapTreeNode),
  };
}

const BASE = '/api/v1/admin/folders';

export const foldersApi = {
  /** Full tree starting from the root. */
  async tree(): Promise<FolderTreeNode[]> {
    const res = await http.get<ActionResponse<FolderTreeNodeBeDto>>(`${BASE}/tree`);
    const { items } = unwrapList(res);
    return items.map(mapTreeNode);
  },

  async get(id: string): Promise<FolderItem> {
    const res = await http.get<ActionResponse<FolderBeDto>>(`${BASE}/${id}`);
    return mapFolder(unwrap(res));
  },

  /** Create a folder. Returns the new folder's id (Guid). */
  async create(name: string, parentFolderId: string | null): Promise<string> {
    const res = await http.post<ActionResponse<string>>(BASE, {
      name,
      parentFolderId,
    });
    return unwrap(res);
  },

  async rename(id: string, newName: string, rowVersion: string | null): Promise<void> {
    const res = await http.put<ActionResponse<boolean>>(`${BASE}/${id}/rename`, {
      newName,
      rowVersion,
    });
    unwrap(res);
  },

  async move(id: string, newParentFolderId: string | null): Promise<void> {
    const res = await http.post<ActionResponse<boolean>>(`${BASE}/${id}/move`, {
      newParentFolderId,
    });
    unwrap(res);
  },

  async remove(id: string): Promise<void> {
    const res = await http.delete<ActionResponse<boolean>>(`${BASE}/${id}`);
    unwrap(res);
  },
};
